import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import {
  ArrowLeft, Bold, Italic, Underline as UnderlineIcon,
  AlignLeft, AlignCenter, AlignJustify,
  List, ListOrdered, Heading1, Heading2, Heading3,
  Undo, Redo, Printer, FileText, Download, X, Sparkles, Eye, Edit3,
} from 'lucide-react';
import { crmApi } from '../../services/crmApi';

// ── Types ──────────────────────────────────────────────────

interface Props {
  applicationId: number;
  docType: string;
  docLabel: string;
  additionalData?: Record<string, string>;
  onClose: () => void;
}

type StudioStep = 'form' | 'editor';

// ── Field config per doc type ──────────────────────────────

const FIELDS_FOR_TYPE: Record<string, string[]> = {
  declaration_private: ['counterpartyName', 'counterpartyAddress', 'serviceDescription'],
  declaration_company: ['counterpartyCompany', 'counterpartyName', 'counterpartyAddress', 'counterpartyNip', 'counterpartyPosition', 'serviceDescription'],
  declaration_wholesaler: ['counterpartyCompany', 'counterpartyName', 'counterpartyAddress', 'counterpartyNip', 'serviceDescription'],
  reference_salon: ['counterpartyCompany', 'counterpartyName', 'counterpartyAddress', 'counterpartyPosition', 'serviceDescription'],
  reference_private: ['counterpartyName', 'counterpartyAddress', 'serviceDescription'],
  spouse_consent: ['spouseFirstName', 'spouseLastName', 'spousePesel', 'serviceDescription'],
  internship_certificate: ['counterpartyCompany', 'counterpartyName', 'counterpartyAddress', 'counterpartyPosition', 'internshipPeriod', 'internshipHours', 'serviceDescription'],
};

const FIELD_LABELS: Record<string, { label: string; placeholder: string }> = {
  counterpartyName: { label: 'Kontrahent', placeholder: 'np. Anna Nowak' },
  counterpartyAddress: { label: 'Adres kontrahenta', placeholder: 'np. ul. Kwiatowa 5, 30-000 Kraków' },
  counterpartyCompany: { label: 'Nazwa firmy', placeholder: 'np. Salon Urody "Bella"' },
  counterpartyNip: { label: 'NIP', placeholder: 'np. 1234567890' },
  counterpartyPosition: { label: 'Stanowisko', placeholder: 'np. Właściciel' },
  spouseFirstName: { label: 'Imię małżonka', placeholder: 'np. Maria' },
  spouseLastName: { label: 'Nazwisko małżonka', placeholder: 'np. Kowalska' },
  spousePesel: { label: 'PESEL małżonka', placeholder: 'np. 85010112345' },
  serviceDescription: { label: 'Opis usługi (opcjonalnie)', placeholder: 'np. usługi kosmetyczne' },
  internshipPeriod: { label: 'Okres praktyk', placeholder: 'np. 01.01-31.03.2025' },
  internshipHours: { label: 'Godziny', placeholder: 'np. 480h' },
};

// ── Component ──────────────────────────────────────────────

export function DocumentStudio({ applicationId, docType, docLabel, additionalData: initialData, onClose }: Props) {
  const [step, setStep] = useState<StudioStep>('form');
  const [formData, setFormData] = useState<Record<string, string>>(initialData || {});
  const [previewHtml, setPreviewHtml] = useState('');
  const [metadata, setMetadata] = useState<{ title: string; fileName: string; applicant: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState<string | null>(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const printRef = useRef<HTMLIFrameElement>(null);

  const fields = FIELDS_FOR_TYPE[docType] || [];

  // TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[400px] p-4',
      },
    },
  });

  // Fetch HTML preview (debounced)
  const fetchPreview = useCallback(async (data: Record<string, string>) => {
    setLoading(true);
    setError('');
    try {
      const result = await crmApi.generateDocumentHTML(applicationId, docType, data);
      setPreviewHtml(result.html);
      setMetadata(result.metadata);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd generowania podglądu');
    } finally {
      setLoading(false);
    }
  }, [applicationId, docType]);

  // Initial load
  useEffect(() => {
    fetchPreview(formData);
  }, []);

  // Debounced field change
  const handleFieldChange = (field: string, value: string) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPreview(newData), 800);
  };

  // Switch to editor step
  const goToEditor = () => {
    if (editor && previewHtml) {
      // Extract body content
      const bodyMatch = previewHtml.match(/<body[^>]*>([\s\S]*)<\/body>/i);
      editor.commands.setContent(bodyMatch ? bodyMatch[1] : previewHtml);
    }
    setStep('editor');
  };

  // Get current HTML from editor
  const getEditorHtml = (): string => {
    if (!editor) return previewHtml;
    const body = editor.getHTML();
    // Wrap in full document with styles
    return previewHtml.replace(/<body[^>]*>[\s\S]*<\/body>/i, `<body>${body}</body>`);
  };

  // Export: Print
  const handlePrint = () => {
    const html = getEditorHtml();
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 300);
    }
  };

  // Export: PDF preview (inline)
  const handlePdfPreview = async () => {
    setExporting('pdf-preview');
    try {
      const html = getEditorHtml();
      const resp = await crmApi.htmlToPdf(html, metadata?.fileName || 'dokument');
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfPreviewUrl(url);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd generowania PDF');
    } finally {
      setExporting(null);
    }
  };

  // Export: Download PDF
  const handleDownloadPdf = async () => {
    setExporting('pdf');
    try {
      const html = getEditorHtml();
      const resp = await crmApi.htmlToPdf(html, metadata?.fileName || 'dokument');
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${metadata?.fileName || 'dokument'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd pobierania PDF');
    } finally {
      setExporting(null);
    }
  };

  // Export: Download DOCX
  const handleDownloadDocx = async () => {
    setExporting('docx');
    try {
      const html = getEditorHtml();
      const resp = await crmApi.htmlToDocx(html, metadata?.fileName || 'dokument');
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${metadata?.fileName || 'dokument'}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd pobierania DOCX');
    } finally {
      setExporting(null);
    }
  };

  // ── Render ──────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-[95vw] max-w-6xl h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 bg-gradient-to-r from-[#1e3a5f] to-[#2a5080] text-white">
          <div className="flex items-center gap-3">
            {step === 'editor' && (
              <button onClick={() => setStep('form')} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
                <ArrowLeft size={18} />
              </button>
            )}
            <div>
              <h2 className="font-bold text-lg">{docLabel}</h2>
              {metadata && <p className="text-xs text-blue-200">{metadata.applicant}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs bg-white/20 px-2.5 py-1 rounded-full">
              {step === 'form' ? 'Krok 1: Formularz + Podgląd' : 'Krok 2: Edytor + Eksport'}
            </span>
            <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mt-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2 text-sm">
            {error}
            <button onClick={() => setError('')} className="ml-2 text-red-500 hover:text-red-700">x</button>
          </div>
        )}

        {/* Step 1: Form + Preview */}
        {step === 'form' && (
          <div className="flex flex-1 overflow-hidden">
            {/* Left: Form */}
            <div className="w-[340px] border-r bg-gray-50 p-5 overflow-y-auto space-y-4">
              <h3 className="font-semibold text-sm text-gray-700 uppercase tracking-wide">Dane dokumentu</h3>

              {fields.map(field => {
                const meta = FIELD_LABELS[field] || { label: field, placeholder: '' };
                return (
                  <div key={field}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{meta.label}</label>
                    {field === 'serviceDescription' ? (
                      <textarea
                        value={formData[field] || ''}
                        onChange={e => handleFieldChange(field, e.target.value)}
                        className="w-full p-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                        placeholder={meta.placeholder}
                        rows={2}
                      />
                    ) : (
                      <input
                        type="text"
                        value={formData[field] || ''}
                        onChange={e => handleFieldChange(field, e.target.value)}
                        className="w-full p-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder={meta.placeholder}
                      />
                    )}
                  </div>
                );
              })}

              <hr className="border-gray-200" />

              <button
                onClick={goToEditor}
                disabled={!previewHtml || loading}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 rounded-lg font-medium text-sm hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 transition-all"
              >
                <Edit3 size={16} />
                Edytuj w WYSIWYG
              </button>

              <div className="flex gap-2">
                <button
                  onClick={handleDownloadPdf}
                  disabled={!previewHtml || loading || !!exporting}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-red-600 text-white py-2.5 rounded-lg font-medium text-xs hover:bg-red-700 disabled:opacity-50"
                >
                  {exporting === 'pdf' ? <Spinner /> : <Download size={14} />}
                  PDF
                </button>
                <button
                  onClick={handleDownloadDocx}
                  disabled={!previewHtml || loading || !!exporting}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-blue-600 text-white py-2.5 rounded-lg font-medium text-xs hover:bg-blue-700 disabled:opacity-50"
                >
                  {exporting === 'docx' ? <Spinner /> : <Download size={14} />}
                  DOCX
                </button>
              </div>
            </div>

            {/* Right: Preview */}
            <div className="flex-1 bg-gray-100 p-4 overflow-auto">
              {loading && !previewHtml ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">Generowanie podglądu...</p>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  {loading && (
                    <div className="absolute top-2 right-2 flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-xs">
                      <div className="animate-spin w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full" />
                      Aktualizacja...
                    </div>
                  )}
                  <div className="bg-white shadow-lg rounded-lg overflow-hidden mx-auto" style={{ maxWidth: '210mm' }}>
                    <iframe
                      srcDoc={previewHtml}
                      className="w-full border-0"
                      style={{ minHeight: '700px', height: '100%' }}
                      title="Podgląd dokumentu"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: WYSIWYG Editor */}
        {step === 'editor' && (
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center gap-1 px-4 py-2 bg-gray-50 border-b flex-wrap">
              <ToolbarBtn onClick={() => editor?.chain().focus().toggleBold().run()} active={editor?.isActive('bold')} title="Pogrubienie">
                <Bold size={16} />
              </ToolbarBtn>
              <ToolbarBtn onClick={() => editor?.chain().focus().toggleItalic().run()} active={editor?.isActive('italic')} title="Kursywa">
                <Italic size={16} />
              </ToolbarBtn>
              <ToolbarBtn onClick={() => editor?.chain().focus().toggleUnderline().run()} active={editor?.isActive('underline')} title="Podkreślenie">
                <UnderlineIcon size={16} />
              </ToolbarBtn>

              <div className="w-px h-6 bg-gray-300 mx-1" />

              <ToolbarBtn onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()} active={editor?.isActive('heading', { level: 1 })} title="Nagłówek 1">
                <Heading1 size={16} />
              </ToolbarBtn>
              <ToolbarBtn onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()} active={editor?.isActive('heading', { level: 2 })} title="Nagłówek 2">
                <Heading2 size={16} />
              </ToolbarBtn>
              <ToolbarBtn onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()} active={editor?.isActive('heading', { level: 3 })} title="Nagłówek 3">
                <Heading3 size={16} />
              </ToolbarBtn>

              <div className="w-px h-6 bg-gray-300 mx-1" />

              <ToolbarBtn onClick={() => editor?.chain().focus().setTextAlign('left').run()} active={editor?.isActive({ textAlign: 'left' })} title="Wyrównaj do lewej">
                <AlignLeft size={16} />
              </ToolbarBtn>
              <ToolbarBtn onClick={() => editor?.chain().focus().setTextAlign('center').run()} active={editor?.isActive({ textAlign: 'center' })} title="Wyśrodkuj">
                <AlignCenter size={16} />
              </ToolbarBtn>
              <ToolbarBtn onClick={() => editor?.chain().focus().setTextAlign('justify').run()} active={editor?.isActive({ textAlign: 'justify' })} title="Wyjustuj">
                <AlignJustify size={16} />
              </ToolbarBtn>

              <div className="w-px h-6 bg-gray-300 mx-1" />

              <ToolbarBtn onClick={() => editor?.chain().focus().toggleBulletList().run()} active={editor?.isActive('bulletList')} title="Lista punktowana">
                <List size={16} />
              </ToolbarBtn>
              <ToolbarBtn onClick={() => editor?.chain().focus().toggleOrderedList().run()} active={editor?.isActive('orderedList')} title="Lista numerowana">
                <ListOrdered size={16} />
              </ToolbarBtn>

              <div className="w-px h-6 bg-gray-300 mx-1" />

              <ToolbarBtn onClick={() => editor?.chain().focus().undo().run()} title="Cofnij">
                <Undo size={16} />
              </ToolbarBtn>
              <ToolbarBtn onClick={() => editor?.chain().focus().redo().run()} title="Ponów">
                <Redo size={16} />
              </ToolbarBtn>
            </div>

            {/* Editor area */}
            <div className="flex-1 overflow-auto bg-gray-100 p-6">
              <div className="bg-white shadow-lg rounded-lg mx-auto" style={{ maxWidth: '210mm', minHeight: '297mm' }}>
                <EditorContent editor={editor} />
              </div>
            </div>

            {/* Export bar */}
            <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t">
              <button
                onClick={() => setStep('form')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 text-sm"
              >
                <ArrowLeft size={16} />
                Wróć do formularza
              </button>

              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-1.5 px-4 py-2.5 bg-gray-700 text-white rounded-lg font-medium text-sm hover:bg-gray-800 transition-colors"
                >
                  <Printer size={15} />
                  Drukuj
                </button>
                <button
                  onClick={handlePdfPreview}
                  disabled={!!exporting}
                  className="flex items-center gap-1.5 px-4 py-2.5 bg-orange-600 text-white rounded-lg font-medium text-sm hover:bg-orange-700 disabled:opacity-50 transition-colors"
                >
                  {exporting === 'pdf-preview' ? <Spinner /> : <Eye size={15} />}
                  PDF podgląd
                </button>
                <button
                  onClick={handleDownloadPdf}
                  disabled={!!exporting}
                  className="flex items-center gap-1.5 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium text-sm hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {exporting === 'pdf' ? <Spinner /> : <Download size={15} />}
                  PDF
                </button>
                <button
                  onClick={handleDownloadDocx}
                  disabled={!!exporting}
                  className="flex items-center gap-1.5 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {exporting === 'docx' ? <Spinner /> : <FileText size={15} />}
                  DOCX
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PDF Preview Modal */}
        {pdfPreviewUrl && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg shadow-2xl w-[80vw] h-[85vh] flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b">
                <span className="font-medium text-sm">Podgląd PDF</span>
                <button onClick={() => { URL.revokeObjectURL(pdfPreviewUrl); setPdfPreviewUrl(null); }} className="p-1 hover:bg-gray-200 rounded">
                  <X size={18} />
                </button>
              </div>
              <iframe src={pdfPreviewUrl} className="flex-1 border-0" title="PDF Preview" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────

function ToolbarBtn({ onClick, active, title, children }: { onClick: () => void; active?: boolean; title: string; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${active ? 'bg-blue-100 text-blue-700' : 'text-gray-600'}`}
    >
      {children}
    </button>
  );
}

function Spinner() {
  return <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />;
}
